import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { BsExclamationTriangleFill } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ApiError, BookingRequestsService, CancelBookingRequest } from '../../../api';
import { generateShowFieldErrors } from '../../../utils/forms';
import { bookingRequestsQueryKeys } from '../../query-keys-factories/booking-requests';
import { userQueryKeys } from '../../query-keys-factories/user';
import { handleApiError } from '../../utils/errorhandling';
import { FormTextareaField } from '../FormTextareaField';
import { ServerErrorsAlert } from '../ServerErrorsAlert';

const validationSchema = z.object({
  comment: z.string().optional(),
});

export const CancelationConfirmationModal = ({
  bookingRequestId,
  show,
  onHide,
}: {
  bookingRequestId: string;
  show: boolean;
  onHide: (result: boolean) => void;
}) => {
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    comment?: boolean;
  }>({});

  const {
    control,
    clearErrors,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const queryClient = useQueryClient();
  const mutationBookingCancel = useMutation({
    mutationFn: async (data: CancelBookingRequest) => {
      return await BookingRequestsService.bookingRequestsCancelUpdate({
        requestId: bookingRequestId,
        requestBody: data,
      });
    },
    onSuccess: () => {
      onHide(true);
      queryClient.invalidateQueries(bookingRequestsQueryKeys.all());
      queryClient.invalidateQueries(userQueryKeys.notifications());
    },
  });

  const onSubmit = async (data: CancelBookingRequest) => {
    handleSubmitClick();
    try {
      await mutationBookingCancel.mutateAsync(data);
      toast.success('Assignment Request canceled successfully');
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  return (
    <Form noValidate>
      <Modal show={show} onHide={() => onHide(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <BsExclamationTriangleFill className="text-danger me-2 mb-2 tw:inline" />
            Assignment Cancellation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Are you sure you want to cancel</strong> this Assignment and all associated
            shifts?
          </p>
          <p>A notification will be sent out about the cancellation to the Provider.</p>
          <p>
            <strong>This cannot be undone.</strong> If you make a mistake you will need to enter
            another assignment request to start over.
          </p>

          <FormTextareaField
            label="Comment"
            name="comment"
            placeholder="Enter comments to the Provider about the cancellation"
            rows={5}
            register={register}
            errors={errors}
            serverErrors={serverErrors?.body?.comment}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
          />
          <ServerErrorsAlert
            errors={serverErrors}
            showErrors={showGlobalServerErrors}
            clearErrors={handleClearErrorsAlert}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => onHide(false)}>
            Go Back
          </Button>
          <Button
            variant="danger"
            onClick={(e) => {
              handleSubmit(onSubmit)(e);
              handleSubmitClick();
            }}
          >
            Cancel Assignment
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};
